// This Froala Plugin replaces the standard link Plugin by providing a button
// which opens a modal we control. The user can choose from any of the existing
// linking options we have available in the Zoomforth application and then
// simply insert that link into their editor.

import FroalaEditor from 'froala-editor';

import includes from 'lodash/includes';

import {
  LINK_TYPE_NONE,
  LINK_TYPE_INTERNAL,
  LINK_TYPE_EXTERNAL,
  LINK_TYPE_OVERLAY,
  LINK_TYPE_DOWNLOADABLE,
  VALID_LINK_TYPES
} from 'zoomforth_codesbane_viewer/const/link_types.js';

import {
  getLinkAttrsFromEl
} from 'zoomforth_zfutils/dom_utils.js';

import {
  getInternalLinkUrl,
  getExternalLinkUrl,
  getExternalLinkTarget,
  getDownloadableLinkUrl 
} from 'zoomforth_codesbane_viewer/lib/app_actions.js';

import {
  getReduxStore as getStore
} from 'zoomforth_codesbane_viewer/lib/global_store.js';

import {
  openAdvancedSidebarLinkEditor
} from 'zoomforth_codesbane_editor/action_creators/froala_linking.js';

import {
  PRIMARY_BUTTON_STYLE_CLASSNAME,
  SECONDARY_BUTTON_STYLE_CLASSNAME
} from 'zoomforth_component_lib/froala_editor.jsx';


export const ADVANCED_LINK_EDITOR_PLUGIN = 'zfAdvancedLinkEditorPlugin';
export const ADVANCED_LINK_EDITOR_PLUGIN_ICON = 'zfAdvancedLinkEditorPlugin.icon';
export const ADVANCED_LINK_EDITOR_PLUGIN_EDIT_ICON = 'zfAdvancedLinkEditorPlugin.edit.icon';

export const BUTTON_TYPE_PRIMARY = 'primary';
export const BUTTON_TYPE_SECONDARY = 'secondary';

// Define the plugin to be used when clicking the custom Froala toolbar button
export const advancedLinkEditorPlugin = (editor) => {
  // Return early if editor is null or undefined
  if (!editor) {
    return {
      getHtml: () => '',
      finishEditing: () => {},
      saveLinkInEditor: () => {},
      removeLinkInEditor: () => {},
      updateSelectionInEditor: () => {},
      restorePreviousHtmlAndFocus: () => {},
      showAdvancedSidebarLinkEditorModal: () => {}
    };
  }

  /*
   * Show the link modal for editing or creating new link.
   */
  function showAdvancedSidebarLinkEditorModal() {
    // save the current selection so we can properly restore it later
    editor.selection.save();

    const editorSelectedLink = editor.link.get();
    const editorSelectedText = editor.selection.text();

    // remove focus from editor so the "edit link" popup can safely disappear
    // also turn off the contenteditable attr on the div so that users can not
    // change the content / HTML of their text. doing so would lead to them
    // losing changes when we restore the previous HTML.
    editor.events.trigger('blur');
    editor.edit.off();

    if (editorSelectedLink) {
      const { href, target, class: className } = getLinkAttrsFromEl(editorSelectedLink);
      const isButton = includes([
        PRIMARY_BUTTON_STYLE_CLASSNAME,
        SECONDARY_BUTTON_STYLE_CLASSNAME
      ], className);
      const text = editorSelectedLink.innerText;
      const buttonType = getButtonType(className);

      // links for some reason are a little weird with selection
      // (most likely due to the default Froala linking plugin).
      // In order to ensure we save the selection for links we set it
      // programatically here.
      editor.selection.setAtStart(editor.link.get());

      getStore().dispatch(openAdvancedSidebarLinkEditor({ text, href, target, isButton, buttonType }));

    } else if (editorSelectedText) {
      getStore().dispatch(openAdvancedSidebarLinkEditor({ text: editorSelectedText }));
    
    } else {
      getStore().dispatch(openAdvancedSidebarLinkEditor({ text: '' }));
    
    }
  }

  function getButtonType(className) {
    return (
      className === PRIMARY_BUTTON_STYLE_CLASSNAME ?
        BUTTON_TYPE_PRIMARY
      : className === SECONDARY_BUTTON_STYLE_CLASSNAME ?
        BUTTON_TYPE_SECONDARY
      :
        null
    );
  }

  function getDataForInsert(appAction, isButton, buttonType) {
    const className = (
      isButton ?
        buttonType === BUTTON_TYPE_PRIMARY ?
          PRIMARY_BUTTON_STYLE_CLASSNAME
        : buttonType === BUTTON_TYPE_SECONDARY ?
          SECONDARY_BUTTON_STYLE_CLASSNAME
        :
          ''
      :
      ''
    );

    // set the URL in the editor instance
    if (appAction.type === LINK_TYPE_NONE) {
      return {
        url: '#',
        attrs: {
          class: className,
          target: '_parent'
        }
      };

    } else if (appAction.type === LINK_TYPE_EXTERNAL) {
      return {
        url: getExternalLinkUrl(appAction),
        attrs: {
          class: className,
          target: getExternalLinkTarget(appAction)
        }
      };

    } else if (includes([LINK_TYPE_INTERNAL, LINK_TYPE_OVERLAY], appAction.type)) {
      return {
        url: getInternalLinkUrl(appAction),
        attrs: {
          class: className,
          target: '_parent'
        }
      };

    } else if (appAction.type === LINK_TYPE_DOWNLOADABLE) {
      return {
        url: getDownloadableLinkUrl(appAction),
        attrs: {
          class: className,
          target: '_parent'
        }
      };

    } else {
      throw new Error(`Invalid appAction type for Froala link: "${appAction.type}" found but expected one of ${VALID_LINK_TYPES.join(', ')}.`);

    }
  }

  /*
   * Save data from advanced link plugin
   *
   * @param editorHTMLWithMarkers string
   * @param appAction object
   * @param text string
   * @param isButton bool
   *
   * @return void
   */
  function saveLinkInEditor(editorHTMLWithMarkers, appAction, text, isButton, buttonType) {
    const { url, attrs } = getDataForInsert(appAction, isButton, buttonType);
    restorePreviousHtmlAndFocus(editorHTMLWithMarkers);
    editor.link.insert(url, text, attrs);
    emitContentChangedEvent();
  }

  /*
   * Update text in the editor while typing in the sidebar
   *
   * @param editorHTMLWithMarkers string
   * @param appAction object
   * @param text string
   * @param isButton bool
   *
   * @return void
   */
  function updateSelectionInEditor(editorHTMLWithMarkers, appAction, text, isButton, buttonType) {
    const { url, attrs } = getDataForInsert(appAction, isButton, buttonType);
    editor.html.set(editorHTMLWithMarkers);
    editor.selection.restore();
    editor.events.trigger('blur');
    editor.link.insert(url, text, attrs);
    editor.selection.restore();
    editor.selection.save();
    editor.edit.off();
    emitContentChangedEvent();
  }

  /*
   * Set the previous HTML for the editor, refocus into it, restore the
   * selection, and ensure the editor is editable.
   *
   * @param previousHtml string
   *
   * @return void
   */
  function restorePreviousHtmlAndFocus(previousHtml) {
    editor.html.set(previousHtml);
    editor.events.focus();
    editor.selection.restore();
    editor.edit.on();
  }

  // HACK: In order to send the contentChanged event after html.set (called
  // implicitly with the above link.insert call) we need to update the undo
  // stack with the new step.
  //
  // https://github.com/froala/wysiwyg-editor/issues/1578
  function emitContentChangedEvent() {
    editor.undo.saveStep();
  }

  /*
   * Delete the selected link
   *
   * @param editorHTMLWithMarkers string
   *
   * @return void
   */
  function removeLinkInEditor(editorHTMLWithMarkers) {
    restorePreviousHtmlAndFocus(editorHTMLWithMarkers);
    editor.link.remove();
    emitContentChangedEvent();
    finishEditing();
  }

  /*
   * Get the HTML from the Froala editor
   *
   * @param bool withMarkers Return HTML with selection markers inserted
   *
   * @return string
   */
  function getHtml(withMarkers = false) {
    // There is a bug in Froala where getting the HTML of the
    // editor, specifically with markers, fails when a <p> tag contains text
    // and another HTML element. They claimed to have fixed it here:
    // https://github.com/froala/wysiwyg-editor/issues/2676
    //
    // Our version of Froala contains this fix however I am seeing it still in
    // our editor.
    //
    // In order to get around this we essentially fallback to what they are
    // actually attempting to do. We get the raw `innerHTML` from the element
    // and return it if their API fails to give it to us. This raw `innerHTML`
    // contains the markers we use to designate where a selection is in the
    // text and where we should insert any changes made in our link editor
    // sidebar.
    try {
      return editor.html.get(withMarkers);
    } catch(err) {
      return editor.$el.get(0).innerHTML;
    }
  }

  /*
   * Finish the link editing experience by ensuring the editor can be used
   * again. When the link editor is opened we turn off this ability so the user
   * does not unexpectantly lose changes via an HTML restore.
   *
   * @return void
   */
  function finishEditing() {
    // Check if editor and edit object exist before trying to call methods
    if (editor && editor.edit) {
      editor.edit.on();
    }
  }

  return {
    getHtml,
    finishEditing,
    saveLinkInEditor,
    removeLinkInEditor,
    updateSelectionInEditor,
    restorePreviousHtmlAndFocus,
    showAdvancedSidebarLinkEditorModal
  };

};


export const initializeAdvancedLinkEditorPlugin = () => {
  FroalaEditor.PLUGINS[ADVANCED_LINK_EDITOR_PLUGIN] = advancedLinkEditorPlugin;

  // Define the custom icons for the toolbar and popup
  FroalaEditor.DefineIcon(ADVANCED_LINK_EDITOR_PLUGIN_ICON , { NAME: 'link', SVG_KEY: 'insertLink'});
  FroalaEditor.DefineIcon(ADVANCED_LINK_EDITOR_PLUGIN_EDIT_ICON , { NAME: 'edit', SVG_KEY: 'edit'});

  // Define toolbar icon and command
  FroalaEditor.RegisterCommand(ADVANCED_LINK_EDITOR_PLUGIN, {
    title: 'Edit Link',
    icon: ADVANCED_LINK_EDITOR_PLUGIN_ICON,
    undo: false,
    focus: true,
    plugin: ADVANCED_LINK_EDITOR_PLUGIN,
    callback: function() {
      this[ADVANCED_LINK_EDITOR_PLUGIN].showAdvancedSidebarLinkEditorModal();
    }
  });

  FroalaEditor.RegisterCommand(ADVANCED_LINK_EDITOR_PLUGIN_EDIT_ICON, {
    title: 'Edit Link',
    undo: false,
    focus: true,
    plugin: ADVANCED_LINK_EDITOR_PLUGIN,
    callback: function() {
      this[ADVANCED_LINK_EDITOR_PLUGIN].showAdvancedSidebarLinkEditorModal();
    }
  });
};
